import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  FeedStatsFilters,
  FeedStatsGrouping,
  FeedStatsRow,
} from '@/features/action-card-feed/action-card-feed-types';
import {
  apiErrorMessage,
  type ApiErrorBody,
} from '@/features/proactivity-experiments/useProactivityExperiments';

export const useActionCardFeedStats = (
  groupBy: FeedStatsGrouping,
  filters: FeedStatsFilters
) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['action-card-feed-stats', groupBy, filters],
    // The server counts every row on each read, so a grouping shown again within five minutes reuses its stats
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<FeedStatsRow[]> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams(
        [
          ['group_by', groupBy],
          ['start', filters.start],
          ['end', filters.end],
          ['user', filters.user],
        ].filter(([, value]) => value !== '')
      );
      const response = await apiFetch(
        `${API_URL}/admin/action-cards/feed-stats?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const body = (await response
          .json()
          .catch(() => null)) as ApiErrorBody | null;
        throw new Error(apiErrorMessage(body, 'Failed to load the feed stats'));
      }
      return response.json() as Promise<FeedStatsRow[]>;
    },
  });
};
